"use client";

import { getUser } from "@opensea/embedded-wallet-sdk";
import { useToasts } from "@opensea/ui-kit";
import { isAddressEqual } from "@opensea/utils";
import { EMBEDDED_WALLET_CONNECTOR_ID, useConnections, useSignWithConnector } from "@opensea/wallet";
import { isError } from "es-toolkit";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useProgress } from "@/hooks/api/use-progress";
import { useAuthenticatedCallback } from "@/hooks/use-authenticated-callback";
import { accountsAdapter } from "@/providers/WalletProvider/accounts-adapter";
export const useLinkWallet = ({
  address,
  onLinked,
  onAlreadyLinked
}) => {
  const t = useTranslations("useLinkWallet");
  const {
    showErrorMessage,
    showSuccessMessage
  } = useToasts();
  const signWithConnector = useSignWithConnector();
  const [isLinking, setIsLinking] = useState(false);
  const connections = useConnections();
  const {
    refetch: refetchProgress
  } = useProgress();
  const linkWallet = useAuthenticatedCallback(async ({
    force
  } = {}) => {
    setIsLinking(true);
    try {
      const connection = connections.find(c => c.addresses.some(a => isAddressEqual(a, address)));
      if (!connection) {
        showErrorMessage(t("couldNotFindConnection"));
        return;
      }
      let result;
      if (connection.connector.id === EMBEDDED_WALLET_CONNECTOR_ID) {
        const user = await getUser();
        result = await accountsAdapter.linkPrivyWallet({
          token: user.accessToken,
          force
        });
      } else {
        const message = await accountsAdapter.createSIWXMessage(address, connection);
        const signature = await signWithConnector({
          connector: connection.connector,
          address,
          message
        });
        result = await accountsAdapter.linkWallet({
          connection,
          signature,
          message,
          force
        });
      }
      if (result.success) {
        showSuccessMessage(t("walletLinkedSuccessfully"), {
          duration: 10_000
        });
        refetchProgress();
        onLinked();
      } else {
        if (onAlreadyLinked && result.code === "ALREADY_LINKED") {
          onAlreadyLinked();
          return;
        }
        showErrorMessage(result.message ?? t("failedToLinkWallet"), {
          duration: 10_000
        });
      }
    } catch (err) {
      showErrorMessage(isError(err) ? err.message : t("failedToLinkWallet"), {
        duration: 10_000
      });
    } finally {
      setIsLinking(false);
    }
  });
  return {
    linkWallet,
    isLinking
  };
};